import { ContentType } from 'api/contentful/contentTypes.cloverdotcom';
import React, { FC } from 'react';
import { TextV2 } from 'components/TextV2/TextV2';
import { useDCSProductsQuery } from 'hooks/DCS/useDCSProductsQuery';
import { useDCSSoftwarePlansQuery } from 'hooks/DCS/useDCSSoftwarePlansQuery';
import { useTranslation } from 'react-i18next';
import {
  getSoftwareMonthlyPricing,
  getProductsTotalSubscriptionPrice,
  getProductsTotalUpfrontPrice,
} from 'utils/DCSUtils';
import { useSubscriptionsState } from 'hooks/useSubscriptionsState';
import { IBYOPricingProps } from './BYOPricingUtils';
import { Pricing } from 'components/Pricing/PricingComponents/Pricing';
import { PRICING } from 'components/Pricing/utils/pricingHookTranslationStrings';
import { useBuildYourOwn } from '../../hooks/useBuildYourOwn';
import { SystemPreviewDrawerPricing } from './SystemPreviewDrawerPricing';

export const BYOItemPricing: FC<IBYOPricingProps> = ({
  dcsIds,
  contentType,
  quantity = 1,
  isSystemPreviewDrawer,
}) => {
  const { t } = useTranslation();
  const { data: dcsProducts } = useDCSProductsQuery();
  const { data: dcsSoftwarePlans } = useDCSSoftwarePlansQuery();
  const { subscriptionsState } = useSubscriptionsState();
  const { softwarePlanCost } = useBuildYourOwn();

  const isProductModule = contentType === ContentType.PRODUCT_MODULE;

  if (!dcsIds.length) return null;

  const getMonthlySoftwarePrice = () => {
    if (isSystemPreviewDrawer) {
      return softwarePlanCost;
    } else if (dcsSoftwarePlans) {
      return getSoftwareMonthlyPricing(dcsIds[0], dcsSoftwarePlans, []);
    }
    return 0;
  };

  const upfrontPrice = getProductsTotalUpfrontPrice(dcsIds, dcsProducts) * quantity;
  const subscriptionMonthlyPrice = getProductsTotalSubscriptionPrice(dcsIds, subscriptionsState, dcsProducts);
  const subscriptionMonthlyPriceWithQuantity = subscriptionMonthlyPrice * quantity;
  const monthlySoftwarePrice = getMonthlySoftwarePrice();

  if (isProductModule) {
    return isSystemPreviewDrawer ? (
      <SystemPreviewDrawerPricing
        upfrontPrice={upfrontPrice}
        subscriptionHardwareMonthlyPrice={subscriptionMonthlyPriceWithQuantity}
      />
    ) : (
      <Pricing
        upfrontPrice={upfrontPrice}
        subscriptionMonthlyPrice={subscriptionMonthlyPrice}
        msrpPrice={0}
        softwarePlanMonthlyPrice={0}
      />
    );
  }

  return (
    <TextV2 variant="body-medium" value={t(PRICING.STRING_MONTHLY_PRICE, { price: monthlySoftwarePrice })} />
  );
};
